/*
 * Requires:
 * - files property
 * - resultAsArray property
 * - fileTypeError property
 * - maxFiles property
 */

import { mapGetters } from 'vuex';

function getDocument(pdfData) {
  // Using import statement in this way allows Webpack
  // to treat pdf.js as an async dependency so we can
  // avoid adding it to one of the main bundles
  return import(
    /* webpackChunkName: 'pdfjs-dist' */
    'pdfjs-dist/webpack'
  ).then((pdfjs) => pdfjs.getDocument(pdfData));
}

export default {
  props: {
    ga: Object,
    context: String,
    inputName: {
      type: String,
    },
    supportMimeTypes: Object,
    supportAnyMimeType: Boolean,
  },
  created() {
    this.files = this.value
      ? this.resultAsArray
        ? this.value
        : this.value.files
      : [];
  },
  computed: {
    ...mapGetters(['access_token']),
    allowedMimeTypes() {
      return (file) => {
        if (this.supportAnyMimeType) return true;
        if (this.supportMimeTypes && this.supportMimeTypes.length) {
          return this.supportMimeTypes.reduce((mrgdCond, mimeType) => {
            return mrgdCond && file.type != mimeType;
          }, true);
        }
        return file.type.startsWith('image/') || file.type == 'application/pdf';
      };
    },
  },
  watch: {
    value(newValue) {
      if (newValue) {
        if (newValue.length == 0 && this.files.length != 0) {
          this.files = [];
        } else if (newValue.length && this.files.length == 0) {
          this.files = newValue;
        }
      }
    },
  },
  methods: {
    fileSelected(event) {
      const selectedFiles = event.target.files;
      this.fileTypeError = false;
      for (let i = 0; i < selectedFiles.length; i++) {
        if (this.maxFiles > 0 && this.files.length + i >= this.maxFiles) {
          break;
        }
        const file = selectedFiles[i];
        if (!this.allowedMimeTypes(file)) {
          this.fileTypeError = true;
          continue;
        }
        const allowPdfResample = this.context == 'partner';
        const fileSizeMb = file.size / 1024 / 1024;
        if (file.type.startsWith('image/') && fileSizeMb > 2) {
          this.addFileResamped(file);
        } else if (
          file.type == 'application/pdf' &&
          fileSizeMb > 2 &&
          allowPdfResample
        ) {
          this.addPDFResampled(file);
        } else {
          this.addFileAsIs(file);
        }
      }
      this.gaTrack();
    },
    addFileResamped(file) {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const elem = document.createElement('canvas');
          const aspect = img.width / img.height;
          const sizeLimit = 1500;
          let width;
          let height;
          if (img.width > sizeLimit || img.height > sizeLimit) {
            if (aspect > 1) {
              width = sizeLimit;
              height = width / aspect;
            } else {
              height = sizeLimit;
              width = height * aspect;
            }
          } else {
            width = img.width;
            height = img.height;
          }
          elem.width = width;
          elem.height = height;
          const ctx = elem.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = ctx.canvas.toDataURL('image/jpeg', 0.8);
          const fileInfo = dataUrl.match(/data:(.*);base64,(.*)/);
          this.files.push({
            data: fileInfo[2],
            mimetype: fileInfo[1],
            resampled: true,
            comment: null,
          });
        };
      };
      fileReader.readAsDataURL(file);
    },
    addFileAsIs(file) {
      const fileReader = new FileReader();
      const populateFile = (event, _file) => {
        const fileInfo = event.target.result.match(/data:(.*);base64,(.*)/);
        this.files.push({
          data: fileInfo[2],
          mimetype: fileInfo[1],
          comment: null,
          size: file.size,
          name: file.name,
          ...(this.selectedJmf ? { variant: this.selectedJmf } : {}),
        });
      };
      fileReader.addEventListener('load', (event) => {
        return populateFile(event, file);
      });
      fileReader.readAsDataURL(file);
    },
    addPDFResampled(file) {
      const fileReader = new FileReader();
      fileReader.addEventListener('load', (event) => {
        const pdfData = event.target.result;
        getDocument(pdfData)
          .then((pdf) => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            let pageNum = 1;
            const renderPage = () => {
              pdf.getPage(pageNum).then((page) => {
                const desiredHeight = 1500;
                let viewport = page.getViewport(1);
                const scale = desiredHeight / viewport.height;
                viewport = page.getViewport(scale);
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                var renderContext = {
                  canvasContext: context,
                  viewport: viewport,
                };
                page.render(renderContext).then(() => {
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  const fileInfo = dataUrl.match(/data:(.*);base64,(.*)/);
                  if (fileInfo && fileInfo.length > 1) {
                    this.files.push({
                      data: fileInfo[2],
                      mimetype: fileInfo[1],
                      resampled: true,
                      comment: null,
                    });
                  }
                  if (pageNum < pdf.numPages) {
                    pageNum += 1;
                    renderPage();
                  }
                });
              });
            };
            renderPage();
          })
          .catch(() => {
            this.addFileAsIs(file);
          });
      });
      fileReader.readAsArrayBuffer(file);
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    gaTrack() {
      if (!this.ga) return;
      const imageTypeToName = {
        before: 'Image Before',
        after: 'Image After',
        protocol: 'Protocol',
        planning_customer: 'Image Planning Customer',
        planning_partner: 'Image Planning Partner',
      };
      this.$gtag.event(
        this.ga.imagesType == 'protocol' ? 'Protocol upload' : 'Image upload',
        {
          event_category: 'Document job',
          event_label: `${imageTypeToName[this.ga.imagesType]} ${
            this.ga.opportunityName
          }`,
        },
      );
    },
  },
};
